import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Bell } from 'lucide-react';
import { supabase } from '../supabaseClient';
import wingitDesign from '../designSystem';

const priorityTone = {
  information: {
    label: 'Information',
    color: '#57d5ff',
    background: 'rgba(87,213,255,0.10)',
    border: 'rgba(87,213,255,0.30)',
  },
  reminder: {
    label: 'Reminder',
    color: '#f2b84b',
    background: 'rgba(242,184,75,0.10)',
    border: 'rgba(242,184,75,0.30)',
  },
  important: {
    label: 'Important',
    color: '#f26d6d',
    background: 'rgba(242,109,109,0.10)',
    border: 'rgba(242,109,109,0.30)',
  },
};

export default function DashboardNoticeBoard({
  organisationId,
}) {
  const styles = getStyles();

  const resolvedOrganisationId = String(
    organisationId || ''
  ).trim();

  const [notices, setNotices] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const loadNotices = useCallback(async () => {
    setLoading(true);
    setStatus('');

    if (!resolvedOrganisationId) {
      setNotices([]);
      setLoading(false);
      return;
    }

    const today = new Date()
      .toISOString()
      .split('T')[0];

    const { data, error } = await supabase
      .from('club_notices')
      .select(
        'id, message, priority, expires_at, created_at, organisation_id'
      )
      .eq(
        'organisation_id',
        resolvedOrganisationId
      )
      .or(
        `expires_at.is.null,expires_at.gte.${today}`
      )
      .order('created_at', {
        ascending: false,
      });

    if (error) {
      setStatus('Could not load club notices.');
      setLoading(false);
      return;
    }

    setNotices(data || []);
    setLoading(false);
  }, [resolvedOrganisationId]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const visibleNotices = expanded
    ? notices
    : notices.slice(0, 3);

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.iconWrap}>
            <Bell size={20} strokeWidth={2.2} />
          </div>

          <div>
            <div style={styles.eyebrow}>CLUB</div>

            <h3 style={styles.title}>
              Notice Board
            </h3>

            <p style={styles.subtitle}>
              Current messages for members.
            </p>
          </div>
        </div>

        {notices.length > 3 && (
          <button
            type="button"
            onClick={() =>
              setExpanded((previous) => !previous)
            }
            style={styles.linkButton}
          >
            {expanded
              ? 'Show less'
              : `Show all (${notices.length})`}
          </button>
        )}
      </div>

      {loading ? (
        <div style={styles.empty}>
          Loading notices...
        </div>
      ) : notices.length === 0 ? (
        <div style={styles.empty}>
          No current club notices.
        </div>
      ) : (
        <div style={styles.list}>
          {visibleNotices.map((notice) => {
            const tone =
              priorityTone[notice.priority] ||
              priorityTone.information;

            return (
              <div
                key={notice.id}
                style={{
                  ...styles.notice,
                  borderColor: tone.border,
                  background: tone.background,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      ...styles.priority,
                      color: tone.color,
                    }}
                  >
                    {tone.label}
                  </div>

                  <div style={styles.noticeText}>
                    {notice.message}
                  </div>

                  {notice.expires_at && (
                    <div style={styles.expiry}>
                      Expires {notice.expires_at}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {status && (
        <div style={styles.status}>
          {status}
        </div>
      )}
    </section>
  );
}

function getStyles() {
  const ds = wingitDesign;

  return {
    panel: {
      ...ds.cards.secondary,
      gridColumn: '1 / -1',
      borderRadius: ds.radius.large,
      padding: '1.1rem',
      boxShadow: ds.shadow.card,
    },

    header: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      alignItems: 'flex-start',
      marginBottom: '0.9rem',
    },

    headerLeft: {
      display: 'flex',
      gap: '0.8rem',
      alignItems: 'center',
    },

    iconWrap: {
      width: 42,
      height: 42,
      borderRadius: 14,
      border: `1px solid ${ds.colors.border}`,
      background: ds.colors.primarySoft,
      color: ds.colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 900,
      marginBottom: '0.2rem',
    },

    title: {
      margin: 0,
      color: ds.colors.text,
      fontSize: '1.18rem',
      fontWeight: 900,
      letterSpacing: '-0.02em',
    },

    subtitle: {
      margin: '0.25rem 0 0',
      color: ds.colors.textMuted,
      fontSize: '0.88rem',
    },

    empty: {
      color: ds.colors.textMuted,
      fontSize: '0.9rem',
      background: 'rgba(150,176,207,0.06)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: ds.radius.medium,
      padding: '0.9rem',
    },

    list: {
      display: 'grid',
      gap: '0.55rem',
    },

    notice: {
      border: '1px solid',
      borderRadius: ds.radius.medium,
      padding: '0.78rem',
      boxShadow:
        'inset 0 1px 0 rgba(255,255,255,0.035)',
    },

    priority: {
      fontSize: '0.7rem',
      fontWeight: 950,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      marginBottom: 5,
    },

    noticeText: {
      color: ds.colors.text,
      fontSize: '0.94rem',
      lineHeight: 1.42,
      whiteSpace: 'pre-wrap',
    },

    expiry: {
      color: ds.colors.textMuted,
      fontSize: '0.76rem',
      marginTop: 6,
    },

    linkButton: {
      background: 'transparent',
      color: ds.colors.primary,
      border: 'none',
      fontWeight: 850,
      cursor: 'pointer',
    },

    status: {
      color: '#ffb0b0',
      marginTop: 10,
      fontSize: '0.9rem',
    },
  };
}